/**
 * TrainingReport - Displays Tier 3 policy model training metrics
 *
 * Shows per-target RMSE with baseline comparison, feature importance
 * bar chart, and training data stats. Rendered inside BenchmarkRunDetail
 * when a training_report is present on the benchmark run doc.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

const SECTION_BORDER = '1px solid rgba(155, 89, 182, 0.2)';
const CARD_BG = 'rgba(0, 0, 0, 0.3)';

function MiniStat({ label, value }) {
    return (
        <Box sx={{ textAlign: 'center', minWidth: 80 }}>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.55rem',
                fontWeight: 600,
                color: '#888',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
            }}>
                {label}
            </Typography>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.95rem',
                fontWeight: 700,
                color: '#5fd4c4',
            }}>
                {value}
            </Typography>
        </Box>
    );
}

function TargetRow({ name, info }) {
    const hasBaseline = info.baseline_rmse != null;
    const newRmse = info.new_rmse ?? 0;
    const baselineRmse = info.baseline_rmse ?? 0;
    const delta = info.delta ?? 0;
    const improved = info.improved;

    const deltaColor = improved ? '#5fd4c4' : improved === false ? vortxColors.error : '#888';
    const deltaSymbol = improved ? '▼' : improved === false ? '▲' : '';
    const deltaText = hasBaseline
        ? `${deltaSymbol} ${Math.abs(delta).toFixed(6)}`
        : 'no baseline';

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 0.75,
            borderBottom: SECTION_BORDER,
        }}>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.7rem',
                color: '#aaa',
                width: 130,
                flexShrink: 0,
            }}>
                {name}
            </Typography>
            {hasBaseline && (
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    color: '#666',
                    width: 90,
                    textAlign: 'right',
                }}>
                    {baselineRmse.toFixed(6)}
                </Typography>
            )}
            {hasBaseline && (
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    color: '#666',
                    width: 20,
                    textAlign: 'center',
                }}>
                    →
                </Typography>
            )}
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.7rem',
                fontWeight: 600,
                color: vortxColors.white,
                width: 90,
                textAlign: hasBaseline ? 'right' : 'left',
            }}>
                {newRmse.toFixed(6)}
            </Typography>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.65rem',
                fontWeight: 600,
                color: deltaColor,
                flex: 1,
            }}>
                {deltaText}
            </Typography>
        </Box>
    );
}

function ImportanceBar({ name, value, maxValue }) {
    const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
    const isBuilding = [
        'building_type_encoded', 'climate_zone_encoded',
        'hvac_fraction', 'plug_load_fraction',
    ].includes(name);

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.3,
        }}>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.6rem',
                color: isBuilding ? '#9b59b6' : '#888',
                fontWeight: isBuilding ? 600 : 400,
                width: 180,
                flexShrink: 0,
                textAlign: 'right',
            }}>
                {name}
            </Typography>
            <Box sx={{
                flex: 1,
                height: 12,
                bgcolor: 'rgba(155, 89, 182, 0.08)',
                borderRadius: 0.5,
                overflow: 'hidden',
            }}>
                <Box sx={{
                    width: `${Math.max(pct, pct > 0 ? 2 : 0)}%`,
                    height: '100%',
                    bgcolor: isBuilding ? '#9b59b6' : '#5fd4c4',
                    borderRadius: 0.5,
                    transition: 'width 0.4s ease',
                }} />
            </Box>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.6rem',
                color: value > 0 ? '#aaa' : '#555',
                width: 50,
                textAlign: 'right',
            }}>
                {value > 0 ? value.toFixed(1) : '0'}
            </Typography>
        </Box>
    );
}

export default function TrainingReport({ report }) {
    if (!report) return null;

    const {
        n_scenarios = 0,
        n_features = 0,
        train_size = 0,
        val_size = 0,
        per_target = {},
        feature_importance = {},
        had_baseline = false,
    } = report;

    const importanceEntries = Object.entries(feature_importance);
    const maxImportance = importanceEntries.length > 0
        ? Math.max(...importanceEntries.map(([, v]) => v))
        : 0;

    return (
        <Box sx={{
            bgcolor: CARD_BG,
            border: SECTION_BORDER,
            borderRadius: 1,
            p: 2.5,
            mb: 3,
        }}>
            {/* Section header */}
            <Typography sx={{
                fontFamily: vortxTypography.heading,
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#9b59b6',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 2,
            }}>
                Tier 3 Training Report
            </Typography>

            {/* Stats row */}
            <Box sx={{
                display: 'flex',
                gap: 3,
                mb: 2.5,
                pb: 2,
                borderBottom: SECTION_BORDER,
                flexWrap: 'wrap',
            }}>
                <MiniStat label="Scenarios" value={n_scenarios} />
                <MiniStat label="Features" value={n_features} />
                <MiniStat label="Train" value={train_size} />
                <MiniStat label="Val" value={val_size} />
                <MiniStat
                    label="Baseline"
                    value={had_baseline ? 'Yes' : 'None'}
                />
            </Box>

            {/* Two-column layout: targets + importance */}
            <Box sx={{
                display: 'flex',
                gap: 3,
                flexWrap: 'wrap',
            }}>
                {/* Per-target RMSE */}
                <Box sx={{ flex: '1 1 340px', minWidth: 0 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        color: '#888',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        mb: 1,
                    }}>
                        Per-Target RMSE {had_baseline ? '(baseline → new)' : ''}
                    </Typography>
                    {Object.entries(per_target).map(([name, info]) => (
                        <TargetRow key={name} name={name} info={info} />
                    ))}
                </Box>

                {/* Feature importance */}
                <Box sx={{ flex: '1 1 340px', minWidth: 0 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        color: '#888',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        mb: 1,
                    }}>
                        Feature Importance (gain)
                        {importanceEntries.some(([k]) => [
                            'building_type_encoded', 'climate_zone_encoded',
                            'hvac_fraction', 'plug_load_fraction',
                        ].includes(k)) && (
                            <span style={{ color: '#9b59b6', marginLeft: 8 }}>
                                ● building
                            </span>
                        )}
                    </Typography>
                    {importanceEntries.map(([name, value]) => (
                        <ImportanceBar
                            key={name}
                            name={name}
                            value={value}
                            maxValue={maxImportance}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
